import { combineLatestWith, map, Observable, ReplaySubject, take, tap } from 'rxjs';

import { isolateFileNameWithExtension } from 'shared-utils/file';
import { FileItem, FileSort, FileType, getExtensionsForFileType, StorageItem } from 'shared-utils/file-system';

import {
  CommandCopyFiles,
  CommandDeleteFiles,
  CommandGetFileNames,
  CommandGetFiles,
  CommandGetStorageLocations,
  CommandGetStorageLocationsResponse,
  CommandGetUserFilesPaths,
  CommandMoveFiles,
  FileFilter,
  FileSourceDestination,
  ICommunicationContext,
  UserFilesPaths,
  UserFilesPathType,
} from '../communication';
import { createFileItem, createStorageItem } from './factories';
import { isWithinRootPathScope } from './helpers';

export class DpuFileSystemClient {
  private readonly _userFilesPaths$: ReplaySubject<UserFilesPaths> = new ReplaySubject<UserFilesPaths>(1);
  private _userFilesPathsLoaded = false;

  constructor(private readonly _communicationContext: ICommunicationContext) {
  }

  public fetchUserFilesPaths(): Observable<UserFilesPaths> {
    return this._communicationContext.sendCommand(new CommandGetUserFilesPaths()).pipe(
      take(1),
      map(response => response.userFilesPaths),
      tap((paths) => {
        this._userFilesPathsLoaded = true;
        this._userFilesPaths$.next(paths);
      }),
    );
  }

  public getUserFilesPaths(): Observable<UserFilesPaths> {
    if (!this._userFilesPathsLoaded) {
      return this.fetchUserFilesPaths();
    }

    return this._userFilesPaths$.asObservable();
  }

  public getUserFilesPathByFileType(fileType: UserFilesPathType): Observable<string | undefined> {
    return this.getUserFilesPaths().pipe(
      map(paths => paths.find(userFilePath => userFilePath.type === fileType)?.path),
    );
  }

  public getFileNames(folderPath: string): Observable<string[] | undefined> {
    return this._communicationContext.sendCommand(new CommandGetFileNames(
      { paths: [folderPath] },
    )).pipe(
      map(response => response?.fileNames.map(fileName => isolateFileNameWithExtension(fileName))),
    );
  }

  public getFiles(
    folderPath: string,
    fileTypeFilter: FileType,
    orderBy: FileSort | undefined,
    filters: FileFilter[] | undefined,
  ): Observable<FileItem[]> {
    return this._communicationContext.sendCommand(new CommandGetFiles({
      path: folderPath,
      extensions: getExtensionsForFileType(fileTypeFilter),
      orderBy,
      filters,
    })).pipe(
      map(response => response?.files.map(file =>
        createFileItem(file, folderPath),
      )),
    );
  }

  public getStorageLocations(): Observable<StorageItem[]> {
    return this._communicationContext.sendCommand(new CommandGetStorageLocations(), { key: 'CommandGetStorageLocations', mode: 'single-flight' }).pipe(
      combineLatestWith(this.getUserFilesPathByFileType('root')),
      map(([response, rootPath]: [CommandGetStorageLocationsResponse, string | undefined]) => response?.storageLocations
        .filter(storageLocation => storageLocation.type !== 'fixed' || isWithinRootPathScope(storageLocation, rootPath))
        .map(storageLocation => createStorageItem(storageLocation, rootPath))),
    );
  }

  public moveFiles(fileTransfers: FileSourceDestination[]): Observable<undefined> {
    return this._communicationContext.sendCommand(new CommandMoveFiles({
      files: fileTransfers,
    }));
  }

  public copyFiles(fileCopies: FileSourceDestination[]): Observable<undefined> {
    return this._communicationContext.sendCommand(new CommandCopyFiles({
      files: fileCopies,
    }));
  }

  public deleteFiles(files: string[]): Observable<undefined> {
    return this._communicationContext.sendCommand(new CommandDeleteFiles({ files }));
  }

  public dispose(): void {
    this._userFilesPaths$.complete();
  }
}
